import "./transactions.css";
import {
  profilePurpleIcons,
  profileFilledDarkIcons,
} from "../../utils/globalImages";
import { Search, X } from "lucide-react";
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { Topup } from "../../api/modules/b2droppie.class";
import { formatDateTime } from "../../utils/HelperFunctions";
import Pagination from "../../components/Pagination/Pagination";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";

const TopUps = () => {
  const sectionTopRef = useRef(null);
  const isSortingRef = useRef(false);
  const [topup, setTopup] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchTopups(currentPage, sortBy, search, sortOrder);
    }, 400);

    return () => clearTimeout(delay);
  }, [currentPage, search, sortBy, sortOrder]);

  const fetchTopups = async (
    page = 1,
    sortField = sortBy,
    searchValue = search,
    sortDirection = sortOrder,
  ) => {
    if (!isSortingRef.current) {
      setLoading(true);
    }
    try {
      const res = await Topup(page, searchValue, sortField, sortDirection);
      setTopup(res?.data?.data || []);
      setLastPage(res?.data?.last_page || 1);
      setCurrentPage(res?.data?.current_page || 1);
    } catch (error) {
      console.error("Error fetching topups:", error);
    } finally {
      setLoading(false);
      isSortingRef.current = false;
    }
  };

  const statusBadge = (status) => {
    const normalized = status?.toString()?.trim()?.toUpperCase();

    return (
      <span className={`custom-status-badge ${normalized?.toLowerCase()}`}>
        {normalized}
      </span>
    );
  };

  const handleSort = (field) => {
    isSortingRef.current = true;

    const newOrder = sortBy === field && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(field);
    setSortOrder(newOrder);
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "⇅";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  return (
    <>
      <div
        ref={sectionTopRef}
        className={`transactions-header d-space-between ${isDarkMode ? "dark" : "light"}`}
      >
        <div className="d-space-between w-100">
          <div
            className={`d-start gap-16px ${searchOpen ? "hide-on-mobile-screen" : ""}`}
          >
            <div className="transactions-icon">
              <img
                src={
                  isDarkMode
                    ? profileFilledDarkIcons.topupFilledDark
                    : profilePurpleIcons.topupFilledPurple
                }
                alt="Top Ups"
              />
            </div>
            <div className="transactions-header-text">
              <h1
                className={`transactions-title-header ${isDarkMode ? "text-white" : "text-deep-purple"}`}
              >
                Top Ups
              </h1>
              <p className="transactions-subtitle-header text-light-gray">
                Manage your account balance and payment history
              </p>
            </div>
          </div>
          <div className={`search-wrapper ${searchOpen ? "open" : ""}`}>
            <input
              type="text"
              value={search}
              placeholder="Search"
              className="search-input"
              onChange={(e) => {
                setCurrentPage(1);
                setSearch(e.target.value);
              }}
              onBlur={() => {
                if (!search) {
                  setTimeout(() => setSearchOpen(false), 200);
                }
              }}
            />
            <div
              className="search-toggle"
              onClick={() => {
                setSearchOpen(!searchOpen);
                if (!searchOpen) {
                  setTimeout(() => {
                    document.querySelector(".search-input")?.focus();
                  }, 300);
                }
              }}
            >
              {searchOpen ? <X size={18} /> : <Search size={18} />}
            </div>
          </div>
          {/* <div className="search-wrapper">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              value={search}
              placeholder="Search"
              className="search-input"
              style={{ backgroundColor: "#29262F" }}
              onChange={(e) => {
                setCurrentPage(1);
                setSearch(e.target.value);
              }}
            />
          </div> */}
        </div>
      </div>

      {loading ? (
        <BackdropLoader show={loading} />
      ) : (
        <div
          className={`myaccount-table-container ${
            isDarkMode ? "dark" : "light"
          }`}
        >
          <div className="myaccount-table-wrapper">
            <table className="profile-tables">
              <thead>
                <tr>
                  <th className="ps-4 ">Transaction ID</th>
                  <th onClick={() => handleSort("amount")} className="sortable">
                    Amount {getSortIcon("amount")}
                  </th>
                  <th
                    onClick={() => handleSort("currency")}
                    className="sortable"
                  >
                    Currency{getSortIcon("currency")}
                  </th>
                  <th onClick={() => handleSort("status")} className="sortable">
                    Status{getSortIcon("status")}
                  </th>
                  <th
                    onClick={() => handleSort("created_at")}
                    className="sortable"
                  >
                    Created at{getSortIcon("created_at")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {topup.length > 0 ? (
                  topup.map((item, index) => {
                    return (
                      <tr key={index} className="withdrawal-row">
                        <td
                          style={{
                            borderTopLeftRadius: "24px!important",
                            borderBottomLeftRadius: "24px!important",
                          }}
                        >
                          {item?.uuid || "N/A"}
                        </td>
                        <td>${item?.pay_amount ?? "N/A"}</td>
                        <td>
                          {item?.price_currency
                            ? item.price_currency.toUpperCase()
                            : "N/A"}
                        </td>
                        <td>{statusBadge(item?.payment_status)}</td>
                        <td>{formatDateTime(item?.created_at)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <Pagination
              lastPage={lastPage}
              currentPage={currentPage}
              scrollTargetRef={sectionTopRef}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      )}

      {/* {deposit && (
        <TopUpModalDeposit
          show={show}
          deposit={deposit}
          setTopUpModalShow={setShow}
        />
      )} */}
    </>
  );
};

export default TopUps;
